import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getOncallByDepartment,
    OncallAssignment,
} from '@/modules/Marketing/services'
import { Tooltip, Typogaphy } from 'vnetwork-ui'
import { Phone, Mail, User } from 'lucide-react'
import TimelineView from '@/modules/Oncall/components/Assignments/Timeline'
import dayjs from 'dayjs'
import useAuthStore from '@/stores/useAuthStore'

import { DEPARTMENT_IDS } from '@/constants/departments'

const getDiceBearAvatar = (seed: string) =>
    `https://api.dicebear.com/9.x/thumbs/svg?seed=${encodeURIComponent(seed)}`

const SalesOncallScheduleWidget: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const today = dayjs().format('YYYY-MM-DD')
    const currentEmployee = useAuthStore((state) => state.employee)

    const { data } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.MARKETING, 'sales-oncall-today', today],
        queryFn: () =>
            getOncallByDepartment({
                department_id: DEPARTMENT_IDS.SALES,
                start_date: today,
                end_date: today,
            }),
    })

    const todayAssignment = data?.results?.[0]
    const isCurrentUserOncall =
        todayAssignment?.employee_id === currentEmployee?.employee_id

    return (
        <div className="h-full flex flex-col">
            <div className="flex items-center justify-between flex-shrink-0">
                <Typogaphy className="font-medium text-primary">
                    {t('sales.oncall_schedule')}
                </Typogaphy>
            </div>

            <div className="flex-1 min-h-0 overflow-auto">
                <TimelineView
                    scheduleName={t('sales.oncall_schedule')}
                    showActions={false}
                    defaultWeekRange="1_week"
                />
            </div>

            <div className="pt-2 flex-shrink-0">
                <WhosOncall
                    assignment={todayAssignment}
                    isCurrentUser={isCurrentUserOncall}
                />
            </div>
        </div>
    )
}

const WhosOncall: React.FC<{
    assignment?: OncallAssignment
    isCurrentUser?: boolean
}> = ({ assignment, isCurrentUser }) => {
    const { t } = useTranslation('dashboard')

    if (!assignment) {
        return (
            <div className="bg-gray-50 rounded-md p-3 flex items-center justify-center">
                <span className="text-xs text-gray-400 italic">
                    {t('sales.no_oncall_today')}
                </span>
            </div>
        )
    }

    return (
        <div
            className={`rounded-md p-3 ${isCurrentUser ? 'bg-amber-50 border border-amber-300' : 'bg-gray-50'}`}
        >
            <div className="flex items-center gap-1.5 mb-2">
                <User
                    size={12}
                    className={
                        isCurrentUser ? 'text-amber-600' : 'text-primary'
                    }
                />
                <span
                    className={`text-xs font-medium ${isCurrentUser ? 'text-amber-600' : 'text-primary'}`}
                >
                    {t('sales.whos_oncall_today')}
                </span>
                {isCurrentUser && (
                    <span className="ml-auto bg-amber-400 text-amber-900 text-[10px] font-semibold px-1.5 py-0.5 rounded">
                        {t('sales.its_you')}
                    </span>
                )}
            </div>
            <div className="flex items-center gap-2">
                <img
                    src={getDiceBearAvatar(
                        assignment.employee_email_company ||
                            assignment.employee_name
                    )}
                    alt={assignment.employee_name}
                    className={`w-10 h-10 rounded-full border-2 flex-shrink-0 ${isCurrentUser ? 'border-amber-500' : 'border-green-500'}`}
                />
                <div className="min-w-0 flex-1">
                    <div
                        className={`font-medium text-sm truncate ${isCurrentUser ? 'text-amber-800' : 'text-gray-800'}`}
                    >
                        {assignment.employee_name}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                        {assignment.employee_phone_number && (
                            <Tooltip title={assignment.employee_phone_number}>
                                <a
                                    href={`tel:${assignment.employee_phone_number}`}
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    <Phone size={12} />
                                </a>
                            </Tooltip>
                        )}
                        {assignment.employee_email_company && (
                            <Tooltip title={assignment.employee_email_company}>
                                <a
                                    href={`mailto:${assignment.employee_email_company}`}
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    <Mail size={12} />
                                </a>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SalesOncallScheduleWidget
